import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  Renderer2,
  OnInit
} from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { IOHFilterFormService } from '../../../../services/filter/ioh-filter-form.service';
import { UserPreferencesService } from 'src/app/services/user-preferences.service';
import {
  FiltersPreferences,
  UserPreferences
} from 'src/app/models/train-info.model';
import { MessageService } from 'primeng/api';
import { IOHSetLevelValueService } from 'src/app/shared/service/ioh-set-level.service';
import { IOHMapService } from 'src/app/services/ioh-map.service';
import { ActivatedRoute } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { IOHFilterTrainListPipe } from 'src/app/shared/pipe/ioh-filter-train-list.pipe';
import {
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  FormControl
} from '@angular/forms';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { IOHFilterExcelService } from 'src/app/services/filter/ioh-filter-excel.service';

@Component({
  selector: 'csx-normal-mode-ioh-filter',
  templateUrl: './normal-mode-ioh-filter.component.html',
  styleUrls: ['./normal-mode-ioh-filter.component.scss'],
  imports: [
    AutoCompleteModule,
    AccordionModule,
    CheckboxModule,
    RadioButtonModule,
    CommonModule,
    TitleCasePipe,
    ReactiveFormsModule,
    FormsModule,
    BadgeModule,
    ButtonModule,
    ToastModule,
    IOHFilterTrainListPipe
  ],
  providers: [IOHFilterTrainListPipe],
  standalone: true
})
export class NormalModeIOHFilterComponent implements OnInit {
  level = input<number>(0);
  showFilterChange = output<boolean>();
  iohShowFilter = signal(false);
  activeTrainList = signal<string[]>([]);
  filteredTrainList = signal<string[]>([]);
  checkForActiveTrainInList = signal<string[] | null>(null);
  searchIdModel = signal('');
  latestTrainList = signal<[string[], string[]]>([[], []]);
  iohLevelValueFromStore = signal<string>('0');
  performanceBadgeValues = signal<string[]>([]);
  commodityBadgeValues = signal<string[]>([]);
  lateSubFilterBadgeValues = signal<string[]>([]);
  isLateSelected = signal<boolean>(false);
  isTrainsFormModified = signal(false);
  readonly isSaveEnabled = computed(() => this.isTrainsFormModified());
  private queryParams = toSignal(this.route.queryParams, { initialValue: {} });
  performanceGroup = 'performance';
  commoditiesGroup = 'commodities';
  selectedTrainGroup = signal<string>(''); // Default to 'All Trains'

  constructor(
    public iohFilterFormService: IOHFilterFormService,
    public iohAppService: IOHAppService,
    private iohMapService: IOHMapService,
    private messageService: MessageService,
    private userPreferencesService: UserPreferencesService,
    private iohSetLevelValueService: IOHSetLevelValueService,
    private route: ActivatedRoute,
    private renderer: Renderer2,
    private iohFilterTrainListPipe: IOHFilterTrainListPipe,
    private iohFilterExcelService: IOHFilterExcelService
  ) {
    effect(() => {
      const data = this.iohAppService._iohActiveTrains();
      this.latestTrainList.set([[...data[0]], [...data[1]]]);
      this.activeTrainList.set([...new Set([...data[0], ...data[1]])]);
    });

    effect(() => {
      this.iohLevelValueFromStore.set(
        this.iohSetLevelValueService.iohSelectedLevel() as string
      );
    });

    effect(() => {
      const params = this.queryParams();
      const filterQueryName = params['query']?.toLowerCase();
      if (filterQueryName) {
        this.applyFilterQuery(filterQueryName);
      }
    });

    effect(() => {
      this.level();
      this.updateBadgeValues();
    });

    //re-apply the saved default filter once the form group is rebuilt after initial data load
    this.iohFilterFormService.dataReady$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.restoreSavedDefaultFilter();
        this.updateBadgeValues();
      });
  }

  ngOnInit(): void {
    this.restoreSavedDefaultFilter();
    this.updateBadgeValues();
  }

  //loads the saved default filter (if any) and applies it to the filter form
  restoreSavedDefaultFilter(): void {
    const userPrefs = this.userPreferencesService.loadFromStorage();
    const savedFilter = userPrefs?.filters?.semanticFilter;
    if (savedFilter && Object.keys(savedFilter).length > 0) {
      this.iohFilterFormService.setFilterPreferenceResponse(savedFilter);
      this.selectedTrainGroup.set(savedFilter.trainGroups || '');
      this.iohAppService.iohFilterTrainsByCriteria(
        this.iohFilterFormService.iohFilterFormGroup.value
      );
      this.isTrainsFormModified.set(false);
    }
  }

  updateBadgeValues(): void {
    const level = this.level();
    const searchValue =
      this.iohFilterFormService.iohFilterFormGroup.get(
        'iohInputForSearch'
      )?.value;

    const performanceValues =
      this.iohFilterFormService.trainPerformanceOptions.map((option, index) => {
        const rawCount =
          this.iohAppService.iohFilterTrainsCountByLevel[level][
            option.filterKey
          ] || 0;
        const performanceArray =
          this.iohFilterFormService.iohFilterFormGroup.get(
            'performance'
          ) as FormArray;
        const control = performanceArray.at(index) as FormControl;
        return this.iohFilterTrainListPipe
          .transform(rawCount, control, true, searchValue)
          .toString();
      });
    this.performanceBadgeValues.set(performanceValues);

    // Check if Late is selected (index 3 in performance options)
    const performanceArray =
      this.iohFilterFormService.iohFilterFormGroup.get(
        'performance'
      ) as FormArray;
    const lateControl = performanceArray.at(3) as FormControl;
    const lateSelected = lateControl && lateControl.value && lateControl.value.includes('PERFORMANCE_CLASSIFICATION_LATE');
    this.isLateSelected.set(!!lateSelected);

    // Update late sub-filter badge values
    const lateSubFilterValues =
      this.iohFilterFormService.lateSubFilterOptions.map((option, index) => {
        const rawCount =
          this.iohAppService.iohFilterTrainsCountByLevel[level][
            option.filterKey
          ] || 0;
        const lateSubFiltersArray =
          this.iohFilterFormService.iohFilterFormGroup.get(
            'lateSubFilters'
          ) as FormArray;
        const control = lateSubFiltersArray.at(index) as FormControl;
        return this.iohFilterTrainListPipe
          .transform(rawCount, control, true, searchValue)
          .toString();
      });
    this.lateSubFilterBadgeValues.set(lateSubFilterValues);

    const commodityValues =
      this.iohFilterFormService.commodityFilterOptions.map((option, index) => {
        const rawCount =
          this.iohAppService.iohFilterTrainsCountByLevel[level][
            option.filterKey
          ] || 0;
        const commoditiesArray =
          this.iohFilterFormService.iohFilterFormGroup.get(
            'commodities'
          ) as FormArray;
        const control = commoditiesArray.at(index) as FormControl;
        return this.iohFilterTrainListPipe
          .transform(rawCount, control, true, searchValue)
          .toString();
      });
    this.commodityBadgeValues.set(commodityValues);
  }

  //to apply filter query
  applyFilterQuery(filterQueryName: string): void {
    this.iohMapService.isIohFilterPatched = true;
    // this.selectedTrainGroup.set(filterQuery.trainGroups || '');
    this.iohMapService.iohShowTrainLabels$.next(true);
    this.onFilterChange();
  }

  //on show filter click
  onShowFilterClicked() {
    this.iohShowFilter.update(val => !val);
    this.showFilterChange.emit(this.iohShowFilter());
  }

  //to reset the filter
  resetFilterValue() {
    this.iohFilterFormService.resetFilterFormGroup();
    this.onFilterChange();
    this.checkForActiveTrainInList.set(null);
    this.searchIdModel.set('');
    this.iohAppService.iohSetTrainIdForSearch('');
    this.selectedTrainGroup.set('');
  }

  //to compare the filter changes
  onFilterChange($event?) {
    this.iohFilterFormService.isIohFilterApplied =
      JSON.stringify(this.iohFilterFormService.initialValues) !==
      JSON.stringify(
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );
    this.iohAppService.iohFilterTrainsByCriteria(
      this.iohFilterFormService.iohFilterFormGroup.value
    );
    this.updateBadgeValues();
    this.isTrainsFormModified.set(true);
  }

  //saves the current train filter selection as the user's default filter
  handleSaveFilters(): void {
    const userPrefs = this.userPreferencesService.loadFromStorage();
    const existingSemanticFilter: FiltersPreferences =
      userPrefs?.filters?.semanticFilter || {};

    const rawValue = this.iohFilterFormService.iohFilterFormGroup.getRawValue();
    const semanticFilter: FiltersPreferences = {
      ...existingSemanticFilter,
      performance: rawValue.performance,
      lateSubFilters: rawValue.lateSubFilters,
      commodities: rawValue.commodities,
      trainGroups: rawValue.trainGroups,
      trainStoppage: rawValue.trainStoppage,
      trainLengths: rawValue.trainLengths
    };

    const updates: Partial<UserPreferences> = {
      filters: {
        semanticFilter
      }
    };

    this.userPreferencesService.saveFilters(updates).subscribe({
      next: () => {
        this.isTrainsFormModified.set(false);
        this.messageService.add({
          key: 'iohSaveFilters',
          severity: 'success',
          summary: 'Success',
          detail: 'Filters saved successfully'
        });
      },
      error: error => {
        this.messageService.add({
          key: 'iohSaveFilters',
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save Filters. Please try again.'
        });
        console.error('Error saving filters:', error);
      }
    });
  }

  //to filter train by id
  filterById() {
    let levelToNavigate: number | null = null; //1 => level 1; 2=> Level 2
    let currentLevel: number | null = null;
    const highlightedButton = document.querySelector(
      '.p-togglebutton .p-button.p-highlight'
    ) as HTMLElement | null;
    if (highlightedButton) {
      currentLevel = Number(
        highlightedButton.textContent?.trim()?.split(' ')[1]
      );
    }

    this.checkForActiveTrainInList.set(
      this.activeTrainList().length
        ? this.activeTrainList().filter(
            item =>
              String(item)?.toLowerCase() ===
              this.searchIdModel()?.toLowerCase()
          )
        : null
    );
    if (this.checkForActiveTrainInList()?.length) {
      for (let i = 0; i < this.latestTrainList()[0].length; i++) {
        if (
          this.checkForActiveTrainInList()![0] === this.latestTrainList()[0][i]
        ) {
          levelToNavigate = 1;
        }
      }
      if (levelToNavigate === null || currentLevel === 2) {
        for (let i = 0; i < this.latestTrainList()[1].length; i++) {
          if (
            this.checkForActiveTrainInList()![0] ===
            this.latestTrainList()[1][i]
          ) {
            levelToNavigate = 2;
          }
        }
      }

      this.iohFilterFormService.iohFilterFormGroup.controls[
        'iohInputForSearch'
      ].patchValue(this.searchIdModel());
      this.IohFocusOnTrian();
      this.onFilterChange();
      this.iohAppService.iohSetTrainIdForSearch(this.searchIdModel());
    }
  }

  //on clear search
  clearIdSearch() {
    this.searchIdModel.set('');
    this.checkForActiveTrainInList.set(null);
    this.iohFilterFormService.iohFilterFormGroup.controls[
      'iohInputForSearch'
    ].reset(null);
    this.onFilterChange();
    this.iohFilterFormService.iohFilterFormGroup.updateValueAndValidity();
    this.iohAppService.iohSetTrainIdForSearch('');
    this.messageService.clear();
  }

  //to handle keydown and enter
  handleSelection(event: any) {
    if (event.type === 'keydown' && event.key === 'Enter') {
      if (this.filteredTrainList().length > 0) {
        this.searchIdModel.set(this.filteredTrainList()[0]);
      }
    }
    this.filterById();
  }

  //to filter id for search
  iohFilterSearchForId(event: any) {
    if (
      (event.query && event.query?.length >= 1) ||
      this.searchIdModel().length >= 1
    ) {
      const searchQuery = event.query || this.searchIdModel();
      this.filteredTrainList.set(
        this.activeTrainList().filter(item =>
          String(item)?.toLowerCase().startsWith(searchQuery?.toLowerCase())
        )
      );
    }
  }

  //to focus train on levels
  IohFocusOnTrian() {
    const elementDOMId =
      this.searchIdModel()?.toLowerCase() + 'L' + this.iohLevelValueFromStore();
    const elementDOM = document.getElementById(elementDOMId);
    if (elementDOM) {
      elementDOM.scrollIntoView({
        block: 'center',
        inline: 'center'
      });
      this.renderer.setStyle(elementDOM, 'zoom', '1.4');
      setTimeout(() => {
        this.renderer.removeStyle(elementDOM, 'zoom');
      }, 5000);
    }
  }

  onTrainGroupChange(value: string) {
    this.iohFilterFormService.iohFilterFormGroup.patchValue({
      trainGroups: value
    });
    this.onFilterChange();
  }

  handleDownloadExcel() {
    this.iohFilterExcelService.downloadCurrentLevelData(this.level())
      .catch((error) => {
        console.error('Excel download failed:', error);
      });
  }

  ngOnDestroy(): void {
    this.iohFilterFormService.isIohFilterApplied = false;
  }
}
